import React, { useState, useEffect } from 'react';
import { Delete } from '@mui/icons-material';
import Toggle from '../../../components/common/toggleButton';
import InputField from '../../../components/common/input-field';
import { getNumberPattern } from '../../utils/helper';

interface GradientStop {
  color: string;
  position: number;
}

interface AdvancedGradientPickerProps {
  value?: string;
  onChange: (_gradient: string) => void;
}
interface gradianTypeObject {
  gradianType: gradianType;
}
type gradianType = 'linear' | 'radial';

const parseGradient = (gradient: string) => {
  const type: gradianType = gradient.startsWith('radial') ? 'radial' : 'linear';
  let angle = 90;
  let stops: GradientStop[] = [];

  try {
    if (type === 'linear') {
      const match = gradient.match(/linear-gradient\((\d+)deg,\s*(.*)\)/);
      if (match) {
        angle = parseInt(match[1], 10);
        stops = match[2].split(',').map(s => {
          const [color, pos] = s.trim().split(' ');
          return {
            color,
            position: parseInt(pos.replace('%', ''), 10),
          };
        });
      }
    } else {
      const match = gradient.match(/radial-gradient\(circle,\s*(.*)\)/);
      if (match) {
        stops = match[1].split(',').map(s => {
          const [color, pos] = s.trim().split(' ');
          return {
            color,
            position: parseInt(pos.replace('%', ''), 10),
          };
        });
      }
    }
  } catch {
    stops = [
      { color: '#0000ff', position: 0 },
      { color: '#ff0000', position: 100 },
    ];
  }

  return { type, angle, stops };
};

const GradientPicker: React.FC<AdvancedGradientPickerProps> = ({
  value = 'linear-gradient(90deg, #0000ff 0%, #ff0000 100%)',
  onChange,
}) => {
  const initial = parseGradient(value);

  const [type, setType] = useState<gradianType>(initial.type);
  const [angle, setAngle] = useState(initial.angle);
  const [stops, setStops] = useState<GradientStop[]>(initial.stops);

  useEffect(() => {
    const parsed = parseGradient(value);
    setType(parsed.type);
    setAngle(parsed.angle);
    setStops(parsed.stops);
  }, [value]);
  useEffect(() => {
    if (type === 'linear') {
      onChange(
        `linear-gradient(${angle}deg, ${stops.map(s => `${s.color} ${s.position}%`).join(', ')})`,
      );
    } else {
      onChange(
        `radial-gradient(circle, ${stops.map(s => `${s.color} ${s.position}%`).join(', ')})`,
      );
    }
  }, [stops, angle, type]);

  const addStop = () => {
    setStops([...stops, { color: '#000000', position: 100 }]);
  };

  const removeStop = (index: number) => {
    if (stops.length > 2) {
      setStops(stops.filter((_, i) => i !== index));
    }
  };

  const updateStop = <K extends keyof GradientStop>(
    index: number,
    key: K,
    value: GradientStop[K],
  ) => {
    const newStops = [...stops];
    newStops[index][key] = value;
    setStops(newStops);
  };
  return (
    <div style={{ width: '100%', fontFamily: 'sans-serif' }}>
      <div className="border border-gray-300 rounded p-2 w-full flex flex-col gap-2 ">
        <Toggle<gradianTypeObject>
          value={{ gradianType: type }}
          onChange={value => setType(value?.gradianType)}
          heading={{ label: 'Gradian Type', value: 'gradiant-type' }}
          options={[
            { label: 'Radial', value: 'radial' },
            { label: 'Linear', value: 'linear' },
          ]}
          propName="gradianType"
        />
      </div>
      {type === 'linear' && (
        <div className="flex items-center gap-2 my-2">
          <div className="font-semibold mt-2">Angle°</div>
          <InputField
            value={angle}
            onChange={e => {
              const newValue = getNumberPattern(e);
              if (newValue) {
                setAngle(Number(newValue));
              }
            }}
            className="h-[42px] "
          />
        </div>
      )}

      <div>
        <div className="font-semibold mt-2">Stops</div>
        <div className="flex items-center gap-2 my-3">
          <div className="w-10"></div>
          <div className="w-20 font-semibold ">Color</div>
          <div className="w-30 font-semibold ml-2">Percentage</div>
        </div>
        {stops.map((stop, i) => (
          <div key={i} className="flex items-center gap-2 my-1">
            <input
              type="color"
              id="colorPicker"
              value={stop.color}
              className="w-9 h-10"
              onChange={e => updateStop(i, 'color', e.target.value)}
            />
            <input
              className="rounded-md w-[100px]"
              type="text"
              id="hexColor"
              value={stop.color}
              onChange={e => updateStop(i, 'color', e.target.value)}
            />
            <InputField
              value={stop.position}
              onChange={e => {
                const newValue = getNumberPattern(e);
                if (newValue) {
                  updateStop(i, 'position', Number(newValue));
                }
              }}
              className="h-[42px] !w-16 rounded-md text-center"
            />
            {stops.length > 2 && (
              <Delete
                className="w-4 h-4 text-gray-400 hover:text-red-500 cursor-pointer"
                onClick={e => {
                  e.stopPropagation();
                  removeStop(i);
                }}
              />
            )}
          </div>
        ))}
      </div>

      <button
        onClick={addStop}
        className="mt-2 px-3 py-1  border text-white font-semibold border-gray-300 rounded hover:bg-gray-600">
        + Add Color
      </button>
      <div className="font-semibold mt-2">Result</div>
      <div
        style={{
          marginTop: '12px',
          width: '100%',
          height: '80px',
          borderRadius: '6px',
          border: '1px solid #ccc',
          background:
            type === 'linear'
              ? `linear-gradient(${angle}deg, ${stops.map(s => `${s.color} ${s.position}%`).join(', ')})`
              : `radial-gradient(circle, ${stops.map(s => `${s.color} ${s.position}%`).join(', ')})`,
        }}
      />
    </div>
  );
};

export default GradientPicker;
